// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListFilterAccordionSectionStyles } from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const productData = getProductListDataView(state);
    return {
        loaded: !!productData.value,
        isClearancedProducts: (state.pages.productList.productFilters as any).isClearanced,
        clearanceItemsCount: parseInt((productData as any).properties.clearanceItemsCount, 10) || 0,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListClearanceItemsFilterStyles {
    // Xng-Change
    checkBoxGroup?: CheckboxGroupProps;
    facetCount?: TypographyPresentationProps;
}

export const productListClearanceItemsFilterStyles: ProductListClearanceItemsFilterStyles = {
    checkBoxGroup: {
        ...productListFilterAccordionSectionStyles.checkBoxGroup,
    },
};

const ProductListClearanceItemsFilter = ({
    loaded,
    isClearancedProducts,
    addProductFilters,
    removeProductFilters,
    clearanceItemsCount,
}: Props) => {
    if (!loaded || !clearanceItemsCount) {
        return null;
    }

    const onChangeFacet = (selected: boolean) => {
        if (selected) {
            removeProductFilters({ isClearanced: false } as any);
        } else {
            addProductFilters({ isClearanced: true } as any);
        }
    };

    const selected = isClearancedProducts ?? false;

    // XNG-Changes: change structure
    return (
        <CheckboxGroup {...productListClearanceItemsFilterStyles.checkBoxGroup}>
            <Checkbox
                {...(selected
                    ? productListFilterAccordionSectionStyles.checkBoxSelected
                    : productListFilterAccordionSectionStyles.checkBox)}
                checked={selected}
                onChange={() => onChangeFacet(selected)}
                data-test-selector="facetFilterClearanceItems"
            >
                {translate("Clearance Items")}
                {clearanceItemsCount && (
                    <Typography {...productListClearanceItemsFilterStyles.facetCount}>{`(${clearanceItemsCount})`}</Typography>
                )}
            </Checkbox>
        </CheckboxGroup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListClearanceItemsFilter),
    definition: {
        group: "Product List",
        displayName: "Clearance Items Filter",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
